import { useState, useRef, useEffect } from 'react';
import { X, Check, ChevronDown } from 'lucide-react';
import type { SelectOption } from '@/constants/adPackOptions';

interface MultiSelectTypeaheadProps {
  label: string;
  sublabel?: string;
  options: SelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

export function MultiSelectTypeahead({
  label,
  sublabel,
  options,
  selected,
  onChange,
  placeholder = 'Type to add...',
}: MultiSelectTypeaheadProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(
    (o) =>
      !selected.includes(o.value) &&
      (o.label.toLowerCase().includes(query.toLowerCase()) ||
        (o.description?.toLowerCase().includes(query.toLowerCase()) ?? false)),
  );

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
    setQuery('');
    inputRef.current?.focus();
  };

  const remove = (value: string) => {
    onChange(selected.filter((v) => v !== value));
  };

  const commitFreeText = () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    // Check if it matches an existing option value
    const match = options.find((o) => o.label.toLowerCase() === trimmed.toLowerCase());
    const value = match ? match.value : trimmed;
    if (!selected.includes(value)) {
      onChange([...selected, value]);
    }
    setQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (filtered.length === 1 && query) {
        toggle(filtered[0].value);
      } else {
        commitFreeText();
      }
    } else if (e.key === 'Backspace' && !query && selected.length > 0) {
      remove(selected[selected.length - 1]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const getLabel = (value: string) => {
    const opt = options.find((o) => o.value === value);
    return opt ? opt.label : value;
  };

  return (
    <div ref={containerRef} className="relative">
      <label className="text-sm font-medium text-slate-700 mb-2 block">
        {label}
        {sublabel && <span className="text-xs text-slate-400 ml-2 font-normal">{sublabel}</span>}
      </label>

      {/* Input container */}
      <div
        className={`min-h-[42px] w-full bg-white border rounded-lg px-2 py-1.5 flex flex-wrap gap-1.5 cursor-text transition-all ${
          open
            ? 'border-emerald-500 ring-2 ring-emerald-500/20'
            : 'border-slate-200 hover:border-slate-300'
        }`}
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        {/* Selected chips */}
        {selected.map((value) => (
          <span
            key={value}
            className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md text-xs font-medium px-2 py-0.5"
          >
            {getLabel(value)}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                remove(value);
              }}
              className="hover:text-emerald-900 transition-colors"
            >
              <X size={12} />
            </button>
          </span>
        ))}

        {/* Text input */}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selected.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[80px] border-none focus:ring-0 p-0.5 text-sm bg-transparent outline-none"
        />

        {/* Dropdown toggle */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(!open);
            inputRef.current?.focus();
          }}
          className="self-center ml-auto text-slate-400 hover:text-slate-600 transition-colors"
        >
          <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Dropdown */}
      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.map((option) => {
            const isSelected = selected.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => toggle(option.value)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between transition-colors"
              >
                <div>
                  <span className="font-medium text-slate-700">{option.label}</span>
                  {option.description && (
                    <span className="text-slate-400 ml-2 text-xs">{option.description}</span>
                  )}
                </div>
                {isSelected && <Check size={14} className="text-emerald-500 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
